import React, { useEffect, useRef, useState } from 'react'
import {
  PlayCircleOutlined,
  LineChartOutlined,
  GlobalOutlined,
  SearchOutlined,
  InsertRowLeftOutlined,
  LeftCircleFilled,
  RightCircleFilled,
  RightCircleOutlined,
  LeftCircleOutlined,
} from '@ant-design/icons'
import { TRIGGER_SEARCH } from '@utils/constants'
import styles from './filters.module.css'

const SelectedFilters = ({
  selectedDescriptions = [],
  selectedHeadlines = [],
  selectedLandingPages = [],
  selectedHooks = [],
  selectedFilters = {},
  getFilterData,
  dispatch,
  setSelectedFilters,
  ADVANCED_FILTERS,
  CROSS_ICON_FILTER,
}) => {
  const containerRef = useRef(null)
  const [showBackButton, setShowBackButton] = useState(false)
  const [showNextButton, setShowNextButton] = useState(false)
  const scroll = direction => {
    if (containerRef.current) {
      const scrollAmount = 130 // Adjust this value as needed
      if (direction === 'left') {
        containerRef.current.scrollLeft -= scrollAmount
      } else {
        containerRef.current.scrollLeft += scrollAmount
      }
    }
  }
  const checkScrollPosition = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current
      const buffer = 5
      setShowBackButton(scrollLeft > buffer)
      setShowNextButton(scrollLeft < scrollWidth - clientWidth - buffer)
    }
  }
  useEffect(() => {
    checkScrollPosition()
    const ref = containerRef.current
    if (ref) ref.addEventListener('scroll', checkScrollPosition)
    window.addEventListener('resize', checkScrollPosition)
    return () => {
      if (ref) ref.removeEventListener('scroll', checkScrollPosition)
      window.removeEventListener('resize', checkScrollPosition)
    }
  }, [
    selectedFilters,
    selectedDescriptions,
    selectedHeadlines,
    selectedLandingPages,
    selectedHooks,
  ])

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {showBackButton && (
          <LeftCircleOutlined
            onClick={() => scroll('left')}
            style={{
              cursor: 'pointer',
              color: '#f16434',
              fontSize: '20px',
              marginTop: '2px',
              marginRight: 10,
            }}
          />
        )}

        <div
          className={styles.selectedFiltersContainer}
          ref={containerRef}
          style={{
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            scrollBehavior: 'smooth',
          }}
          onScroll={checkScrollPosition}
        >
          {/* Display selected descriptions */}
          {selectedDescriptions.length > 0 && (
            <div className={styles.selectedFilter}>
              <span className={`${styles.filterLabel} ${styles.filterIcon}`}>
                <PlayCircleOutlined />
                Descriptions
                <span className={styles.filterCount}>{selectedDescriptions.length}</span>
              </span>
              {/* eslint-disable-next-line */}
              <span
                className={styles.removeFilter}
                onClick={() => {
                  dispatch({ type: 'SET_SELECTED_DESCRIPTIONS', payload: [] })
                  dispatch({ type: CROSS_ICON_FILTER, payload: true })
                  setTimeout(() => {
                    dispatch({ type: CROSS_ICON_FILTER, payload: false })
                  }, 300)
                }}
              >
                ×
              </span>
            </div>
          )}
          {/* Display selected headlines */}
          {selectedHeadlines.length > 0 && (
            <div className={styles.selectedFilter}>
              <span className={`${styles.filterLabel} ${styles.filterIcon}`}>
                <LineChartOutlined />
                Headlines
                <span className={styles.filterCount}>{selectedHeadlines.length}</span>
              </span>
              {/* eslint-disable-next-line */}
              <span
                className={styles.removeFilter}
                onClick={() => {
                  dispatch({ type: 'SET_SELECTED_HEADLINES', payload: [] })
                  dispatch({ type: CROSS_ICON_FILTER, payload: true })
                  setTimeout(() => {
                    dispatch({ type: CROSS_ICON_FILTER, payload: false })
                  }, 300)
                }}
              >
                ×
              </span>
            </div>
          )}
          {/* Display selected landing pages */}
          {selectedLandingPages.length > 0 && (
            <div className={styles.selectedFilter}>
              <span className={`${styles.filterLabel} ${styles.filterIcon}`}>
                <GlobalOutlined />
                Landing Pages
                <span className={styles.filterCount}>{selectedLandingPages.length}</span>
              </span>
              {/* eslint-disable-next-line */}
              <span
                className={styles.removeFilter}
                onClick={() => {
                  dispatch({ type: 'SET_SELECTED_LANDING_PAGES', payload: [] })
                  dispatch({ type: CROSS_ICON_FILTER, payload: true })
                  setTimeout(() => {
                    dispatch({ type: CROSS_ICON_FILTER, payload: false })
                  }, 300)
                }}
              >
                ×
              </span>
            </div>
          )}
          {/* Display selected hooks */}
          {selectedHooks.length > 0 && (
            <div className={styles.selectedFilter}>
              <span className={`${styles.filterLabel} ${styles.filterIcon}`}>
                <SearchOutlined />
                Selected Hooks
                <span className={styles.filterCount}>{selectedHooks.length}</span>
              </span>
              {/* eslint-disable-next-line */}
              <span
                className={styles.removeFilter}
                onClick={() => {
                  dispatch({ type: 'SET_SELECTED_HOOKS', payload: [] })
                  dispatch({ type: CROSS_ICON_FILTER, payload: true })
                  setTimeout(() => {
                    dispatch({ type: CROSS_ICON_FILTER, payload: false })
                  }, 300)
                }}
              >
                ×
              </span>
            </div>
          )}
          {Object.entries(selectedFilters).map(([category, values]) => {
            if (
              category === 'hooks' ||
              category === 'descriptions' ||
              category === 'headlines' ||
              category === 'landingPages' ||
              values.length === 0
            )
              return null
            return (
              <div key={category} className={styles.selectedFilter}>
                <span className={`${styles.filterLabel} ${styles.filterIcon}`}>
                  {getFilterData()[category]?.icon}
                  {getFilterData()[category]?.name || category}
                  <span className={styles.filterCount}>{values.length}</span>
                </span>
                {/* eslint-disable-next-line */}
                <span
                  className={styles.removeFilter}
                  onClick={() => {
                    const newFilters = {
                      ...selectedFilters,
                      [category]: [],
                    }
                    setSelectedFilters(newFilters)
                    dispatch({
                      type: ADVANCED_FILTERS,
                      payload: newFilters,
                    })
                    dispatch({ type: TRIGGER_SEARCH, payload: true })
                    dispatch({ type: CROSS_ICON_FILTER, payload: true })
                    setTimeout(() => {
                      dispatch({ type: CROSS_ICON_FILTER, payload: false })
                    }, 300)
                    setTimeout(() => {
                      dispatch({ type: TRIGGER_SEARCH, payload: false })
                    }, 300)
                  }}
                >
                  ×
                </span>
              </div>
            )
          })}
        </div>
        {showNextButton && (
          <RightCircleOutlined
            onClick={() => scroll('right')}
            style={{
              marginLeft: 10,
              marginRight: 30,
              cursor: 'pointer',
              fontSize: '20px',
              color: '#f16434',
            }}
          />
        )}
      </div>
    </>
  )
}

export default SelectedFilters
